import { useEffect, useRef } from "react";
import { isKeyboardShortcutSuppressedTarget } from "./keyboardShortcutGuards";
import { useMst } from "../stores/main";

function isSpaceKey(e: KeyboardEvent): boolean {
  return e.code === "Space" || e.key === " " || e.key === "Spacebar";
}

type PlaybackShortcutPlayer = {
  mainContainer?: HTMLElement;
  animation?: { isPaused?: boolean };
  pausedByUser?: boolean;
  play?: () => void;
  pause?: () => void;
};

function isPlayerKeyboardTarget(
  target: EventTarget | null,
  player: PlaybackShortcutPlayer | undefined
): boolean {
  return Boolean(
    target instanceof Node &&
      player?.mainContainer &&
      player.mainContainer.contains(target)
  );
}

function stopHandledSpaceEvent(e: KeyboardEvent): void {
  e.stopPropagation();
  if (typeof e.stopImmediatePropagation === "function") {
    e.stopImmediatePropagation();
  }
}

/**
 * Hook that sets up keyboard shortcut for play/pause:
 * - Space bar toggles video playback
 */
export function usePlaybackShortcuts() {
  const { playerStore } = useMst();
  const suppressNextSpaceKeyUpRef = useRef(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isKeyboardShortcutSuppressedTarget(e.target)) {
        return;
      }

      if (isSpaceKey(e)) {
        const player = (playerStore.player ?? (window as any).p) as
          | PlaybackShortcutPlayer
          | undefined;
        if (isPlayerKeyboardTarget(e.target, player)) {
          return;
        }

        e.preventDefault();
        stopHandledSpaceEvent(e);
        if (e.repeat) {
          return;
        }

        if (player?.animation) {
          if (player.animation.isPaused) {
            player.pausedByUser = false;
            player.play?.();
          } else {
            player.pausedByUser = true;
            player.pause?.();
          }
          suppressNextSpaceKeyUpRef.current = true;
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (isSpaceKey(e) && suppressNextSpaceKeyUpRef.current) {
        suppressNextSpaceKeyUpRef.current = false;
        e.preventDefault();
        stopHandledSpaceEvent(e);
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("keyup", handleKeyUp, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("keyup", handleKeyUp, true);
    };
  }, [playerStore]);
}
